import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Loader2Icon } from 'lucide-react'
import { useState } from 'react'

import { Banner, Button, Checkbox, InputBlock, RadioButton, RadioGroup, SolidCircleCheckIcon } from 'slab'
import {
  getGetEmployersAcaReportingStatusQueryKey,
  usePostEmployersAcaReportingConfirmAleStatus,
  usePostEmployersAcaReportingOptIn,
  usePostEmployersAcaReportingOptOut
} from 'utils/web_api'

import AleQuestionSection from './AleQuestionSection'

interface OptInFormProps {
  planYear: number
  onOptInSuccess: () => void
  onOptOutSuccess: () => void
  originalStatus?: 'opted_in' | 'opted_out' | null
  onCancel?: () => void
}

type FilingPreference = 'thatch_files' | 'self_files' | null

export default function OptInForm ({
  planYear,
  onOptInSuccess,
  onOptOutSuccess,
  originalStatus,
  onCancel
}: OptInFormProps) {
  const [filingPreference, setFilingPreference] = useState<FilingPreference>(null)
  const [aleStatus, setAleStatus] = useState<boolean | null>(null)
  const [irsContactFirstName, setIrsContactFirstName] = useState('')
  const [irsContactLastName, setIrsContactLastName] = useState('')
  const [irsContactEmail, setIrsContactEmail] = useState('')
  const [attestation1, setAttestation1] = useState(false)
  const [attestation2, setAttestation2] = useState(false)
  const [attestation3, setAttestation3] = useState(false)

  const queryClient = useQueryClient()

  const optInMutation = usePostEmployersAcaReportingOptIn({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersAcaReportingStatusQueryKey({ plan_year: planYear })
        })
        onOptInSuccess()
      }
    }
  })

  const optOutMutation = usePostEmployersAcaReportingOptOut({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersAcaReportingStatusQueryKey({ plan_year: planYear })
        })
        onOptOutSuccess()
      }
    }
  })

  const confirmAleStatusMutation = usePostEmployersAcaReportingConfirmAleStatus({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEmployersAcaReportingStatusQueryKey({ plan_year: planYear })
        })
        if (originalStatus === 'opted_out') {
          onOptOutSuccess()
        } else {
          optOutMutation.mutate({
            data: {
              plan_year: planYear,
              reason: 'Will handle filing independently'
            }
          })
        }
      }
    }
  })

  const handleOptInSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (aleStatus === null || filingPreference !== 'thatch_files') return

    optInMutation.mutate({
      data: {
        plan_year: planYear,
        self_reported_ale_status: aleStatus,
        attestation_confirmed: allAttestationsConfirmed,
        irs_contact_first_name: irsContactFirstName,
        irs_contact_last_name: irsContactLastName,
        irs_contact_email: irsContactEmail
      }
    })
  }

  const handleSelfFileSubmit = () => {
    if (aleStatus === null) return

    confirmAleStatusMutation.mutate({
      data: {
        plan_year: planYear,
        ale_status: aleStatus
      }
    })
  }

  const isThatchFiling = filingPreference === 'thatch_files'
  const isSelfFiling = filingPreference === 'self_files'
  const allAttestationsConfirmed = attestation1 && attestation2 && attestation3
  const irsContactComplete = irsContactFirstName.trim() !== '' &&
    irsContactLastName.trim() !== '' &&
    irsContactEmail.trim() !== ''
  const isFormValid = isThatchFiling && aleStatus !== null && irsContactComplete && allAttestationsConfirmed
  const isSubmitting = optInMutation.isPending
  const isConfirmingAleAndOptingOut = confirmAleStatusMutation.isPending || optOutMutation.isPending

  return (
    <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
      <div className='bg-gray-50 border-b border-gray-200 px-6 py-4'>
        <div className='flex items-center gap-3'>
          <SolidCircleCheckIcon className='text-green-500' size={20} />
          <span className='text-sm text-green-500'>You are eligible for Thatch's ACA filing services!</span>
        </div>
      </div>

      <div className='p-6 space-y-6'>
        <div>
          <h2 className='text-sm font-semibold text-gray-900 mb-3'>
            How would you like to handle filing?
          </h2>

          <RadioGroup name='filing_preference'>
            <div className='flex gap-3'>
              <div className={clsx('flex-1 border rounded-lg p-4', {
                'border-blue-500 bg-blue-50': filingPreference === 'thatch_files',
                'border-gray-200': filingPreference !== 'thatch_files'
              })}
              >
                <RadioButton
                  label='I want Thatch to handle filing'
                  description='Thatch will file with the IRS and distribute forms to employees'
                  value='thatch_files'
                  checked={filingPreference === 'thatch_files'}
                  onChange={() => setFilingPreference('thatch_files')}
                />
              </div>
              <div className={clsx('flex-1 border rounded-lg p-4', {
                'border-blue-500 bg-blue-50': filingPreference === 'self_files',
                'border-gray-200': filingPreference !== 'self_files'
              })}
              >
                <RadioButton
                  label="I'll handle filing myself"
                  description='Download the .csv to share with your tax advisor'
                  value='self_files'
                  checked={filingPreference === 'self_files'}
                  onChange={() => setFilingPreference('self_files')}
                />
              </div>
            </div>
          </RadioGroup>

          {filingPreference === null && originalStatus && onCancel && (
            <div className='flex justify-end mt-6'>
              <Button
                type='button'
                variant='secondary'
                onClick={onCancel}
                disabled={isSubmitting || isConfirmingAleAndOptingOut}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {(isThatchFiling || isSelfFiling) && (
          <>
            <div className='pt-4 border-t border-gray-200'>
              <AleQuestionSection
                aleStatus={aleStatus}
                onAleStatusChange={setAleStatus}
                showFormTypeHint
              />
            </div>

            {aleStatus === null && originalStatus && onCancel && (
              <div className='flex justify-end'>
                <Button
                  type='button'
                  variant='secondary'
                  onClick={onCancel}
                  disabled={isSubmitting || isConfirmingAleAndOptingOut}
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}

        {isThatchFiling && aleStatus !== null && (
          <div className='pt-4 border-t border-gray-200 max-w-2xl'>
            <InputBlock
              label={<span className='font-medium text-sm'>IRS Point of Contact</span>}
              description={<span className='text-sm'>This person will be contacted for any follow-up from the IRS and/or our filing assistance partner</span>}
            >
              <InputBlock.Row>
                <InputBlock.Input
                  label='First name'
                  name='irs_contact_first_name'
                  value={irsContactFirstName}
                  onChange={(e) => setIrsContactFirstName(e.target.value)}
                />
              </InputBlock.Row>
              <InputBlock.Row>
                <InputBlock.Input
                  label='Last name'
                  name='irs_contact_last_name'
                  value={irsContactLastName}
                  onChange={(e) => setIrsContactLastName(e.target.value)}
                />
              </InputBlock.Row>
              <InputBlock.Row>
                <InputBlock.Input
                  label='Email'
                  name='irs_contact_email'
                  type='email'
                  value={irsContactEmail}
                  onChange={(e) => setIrsContactEmail(e.target.value)}
                />
              </InputBlock.Row>
            </InputBlock>
          </div>
        )}

        {isThatchFiling && aleStatus !== null && (
          <form onSubmit={handleOptInSubmit} className='space-y-4 pt-4 border-t border-gray-200'>
            <div>
              <h2 className='text-base font-medium text-gray-900 mb-3'>
                Confirm the following
              </h2>

              <div className='space-y-3'>
                <Checkbox
                  label={`My organization has used Thatch for health benefits for the entire ${planYear} plan year (starting January 1)`}
                  checked={attestation1}
                  onChange={(e) => setAttestation1(e.target.checked)}
                />
                <Checkbox
                  label='My organization does not offer group health insurance or other health benefits outside of Thatch'
                  checked={attestation2}
                  onChange={(e) => setAttestation2(e.target.checked)}
                />
                <Checkbox
                  label='I am authorized to make this election on behalf of my organization'
                  checked={attestation3}
                  onChange={(e) => setAttestation3(e.target.checked)}
                />
              </div>
            </div>

            {optInMutation.isError && (
              <Banner type='critical' title='Unable to complete opt-in'>
                {(optInMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
              </Banner>
            )}

            <div className='flex justify-end gap-3'>
              {originalStatus && onCancel && (
                <Button
                  type='button'
                  variant='secondary'
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}
              <Button
                type='submit'
                variant='primary'
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting
                  ? (
                    <span className='flex items-center gap-2'>
                      <Loader2Icon className='w-4 h-4 animate-spin' />
                      Submitting...
                    </span>
                    )
                  : 'Let Thatch handle filing'}
              </Button>
            </div>
          </form>
        )}

        {isSelfFiling && aleStatus !== null && (
          <div className='pt-4 border-t border-gray-200'>
            {confirmAleStatusMutation.isError && (
              <Banner type='critical' title='Unable to confirm ALE status'>
                {(confirmAleStatusMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
              </Banner>
            )}

            {optOutMutation.isError && (
              <Banner type='critical' title='Unable to complete opt-out'>
                {(optOutMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
              </Banner>
            )}

            <div className='flex justify-end gap-3'>
              {originalStatus && onCancel && (
                <Button
                  type='button'
                  variant='secondary'
                  onClick={onCancel}
                  disabled={isConfirmingAleAndOptingOut}
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSelfFileSubmit}
                variant='primary'
                disabled={isConfirmingAleAndOptingOut}
              >
                {isConfirmingAleAndOptingOut
                  ? (
                    <span className='flex items-center gap-2'>
                      <Loader2Icon className='w-4 h-4 animate-spin' />
                      Confirming...
                    </span>
                    )
                  : "I'll handle filing myself"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
